// main - SPA entry: mounts <App> inside the provider tree (query/router/auth/toast/upload).

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import App from '@/App';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ActiveOrgProvider } from '@/components/OrgPicker';
import { UploadWidget } from '@/components/UploadWidget';
import { AuthProvider } from '@/context/AuthContext';
import { ConfirmProvider } from '@/context/ConfirmContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { ToastProvider } from '@/context/ToastContext';
import { UploadProvider } from '@/context/UploadContext';
import '@/index.css';

// Recover from a stale tab after a redeploy: when a lazily-imported route
// chunk 404s (its content hash changed under the open tab), Vite fires
// vite:preloadError. Reload once to pull the fresh index + chunks instead of
// surfacing the error boundary. The timestamp guard breaks any reload loop -
// if a reload doesn't resolve it within 10s the next error is allowed through.
window.addEventListener('vite:preloadError', (event) => {
  const last = Number(sessionStorage.getItem('vite-preload-reloaded') || 0);
  if (Date.now() - last < 10_000) return;
  event.preventDefault();
  sessionStorage.setItem('vite-preload-reloaded', String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5_000,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <ActiveOrgProvider>
                <ToastProvider>
                  <UploadProvider>
                    <ConfirmProvider>
                      <App />
                      <UploadWidget />
                    </ConfirmProvider>
                  </UploadProvider>
                </ToastProvider>
              </ActiveOrgProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
